import { defineRouter } from "#q-app";
import {
    createMemoryHistory,
    createRouter,
    createWebHashHistory,
    createWebHistory
} from "vue-router";
import routes from "./routes";
import { useAuthUserStore } from "@/stores/authUser";
import { useAppConfigStore } from "@/stores/appConfig";
import { errorFromUnknown } from "@/errors";
import { useRetryErrorDialog } from "@/composables/retryErrorDialog";

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default defineRouter(function (/* { store, ssrContext } */) {
    const createHistory = import.meta.env.QUASAR_SERVER
        ? createMemoryHistory
        : import.meta.env.QUASAR_VUE_ROUTER_MODE === "history"
          ? createWebHistory
          : createWebHashHistory;

    const Router = createRouter({
        //scrollBehavior: () => ({ left: 0, top: 0 }),
        routes,

        // Leave this as is and make changes in quasar.conf.js instead!
        // quasar.conf.js -> build -> vueRouterMode
        // quasar.conf.js -> build -> publicPath
        history: createHistory(import.meta.env.QUASAR_VUE_ROUTER_BASE)
    });

    Router.beforeEach(async (to, from) => {
        try {
            const appConfigStore = useAppConfigStore();
            if (!appConfigStore.loaded) {
                await appConfigStore.init();
            }

            if (to.meta.requiredFeatures) {
                for (const feature of to.meta.requiredFeatures) {
                    if (!appConfigStore.features[feature].enabled) {
                        if (from.path === "/") {
                            return { name: "browse", replace: true };
                        }
                        return false;
                    }
                    console.log(`Feature "${feature}" is enabled.`);
                }
            }

            const requiresAuth = to.meta.auth;

            const authUserStore = useAuthUserStore();
            if (requiresAuth === "authOnly" && !authUserStore.isAuthenticated) {
                return { name: "login", query: { redirect: to.fullPath } };
            } else if (
                requiresAuth === "noAuthOnly" &&
                authUserStore.isAuthenticated
            ) {
                if (from.path !== to.path) {
                    return from;
                }
                console.warn(
                    "Navigation prevented: user is already authenticated."
                );
                return { name: "browse", replace: true };
            }
        } catch (e) {
            const { showRetryError } = useRetryErrorDialog();
            const err = errorFromUnknown(e);
            console.error(err);

            showRetryError({
                message: err.display,
                error: err,
                showBack: !(from.path === "/" && from.name === undefined),
                onRetry: () => {
                    window.location.reload();
                },
                onBack: () => {
                    if (from.path) {
                        window.location.href = from.path;
                    }
                }
            });
            return false;
        }
    });

    return Router;
});
